import { Divider, Group, Stack, SelectProps } from "@mantine/core";
import { useActiveUser } from "@/store/selectors";
import {
  useIsLaptopOrBigger,
  useIsTabletOrBigger,
  useStyles,
} from "@/styles/useStyles";
import {
  IconVideo,
  IconUser,
  IconCheckbox,
  IconThumbUp,
  IconSparkles,
  IconUsersGroup,
  IconClock,
  IconStarFilled,
} from "@tabler/icons-react";
import { ResourceConfigs } from "@/configs/resourceConfigs";
import { Analytics } from "@/services/Analytics";
import { Text } from "@/components/ui/Text";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCurrentUserLevel,
  useTotalNegativeVideoReactions,
  useTotalNeutralVideoReactions,
  useTotalPositiveVideoReactions,
  useTotalUserLevels,
  useTotalUserRecommendedAdvizers,
  useUserAdvizersWatched,
  useUserCompletedPlaylistKeys,
  useUserInProgressPlaylistKeys,
  useUserVideosWatched,
} from "@/store/selectors/userProfileSelectors";
import { Select } from "@/components/ui/Select";
import { LoadingScreen } from "@/components/templates/LoadingScreen";
import { Navigation } from "@/services/Navigation";
import {
  useRecommendedPlaylists,
  useUserCanAccessPlaylists,
} from "@/store/selectors/playlistSelectors";
import {
  useUserAssignments,
  useUserIncompleteAssignments,
} from "@/store/selectors/assignmentSelectors";

import { UserProfileAssignments } from "@/components/userProfile/UserProfileAssignments";
import { Title } from "@/components/ui/Title";
import { Link } from "@/components/ui/Link";
import { Badge } from "@/components/ui/Badge";
import { UserProfileVideosWatched } from "@/components/userProfile/UserProfileVideosWatched";
import { UserProfilePlaylists } from "@/components/userProfile/UserProfilePlaylists";
import { UserProfileReactions } from "@/components/userProfile/UserProfileReactions";
import { UserProfileRecommendedPlaylists } from "@/components/userProfile/UserProfileRecommendedPlaylists";
import { UserProfileRecommendedAdvizers } from "@/components/userProfile/UserProfileRecommendedAdvizers";
import { UserProfileLevels } from "@/components/userProfile/UserProfileLevels";

export function UserProfile() {
  const { theme, ...styles } = useStyles();
  const activeUser = useActiveUser();
  const isTabletOrBigger = useIsTabletOrBigger();
  const isLaptopOrBigger = useIsLaptopOrBigger();
  const userVideosWatched = useUserVideosWatched();
  const completedPlaylistKeys = useUserCompletedPlaylistKeys();
  const inProgressPlaylistKeys = useUserInProgressPlaylistKeys();
  const userAdvizersWatched = useUserAdvizersWatched();
  const userIncompleteAssignments = useUserIncompleteAssignments();
  const userCanAccessPlaylists = useUserCanAccessPlaylists();
  const userAssignments = useUserAssignments();
  const userHasAssignments = Object.keys(userAssignments).length > 0;
  const [activeTab, setActiveTab] = useState<string>(
    userHasAssignments
      ? "assignments"
      : userCanAccessPlaylists
        ? "recommendations"
        : "videos",
  );

  const totalPositiveVideoReactions = useTotalPositiveVideoReactions();
  const totalNeutralVideoReactions = useTotalNeutralVideoReactions();
  const totalNegativeVideoReactions = useTotalNegativeVideoReactions();
  const recommendedPlaylists = useRecommendedPlaylists();
  const totalUserRecommendedAdvizers = useTotalUserRecommendedAdvizers();
  const totalUserLevels = useTotalUserLevels();
  const currentUserLevel = useCurrentUserLevel();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const stickyTabsRef = useRef<HTMLDivElement>(null);

  // Scroll to section by id within the scroll container
  const scrollToSection = useCallback(
    (sectionId: string) => {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        const container = scrollContainerRef.current;
        const stickyTabs = stickyTabsRef.current;
        if (element && container) {
          // Get the element's position relative to the container
          const elementOffsetTop = element.offsetTop;
          // Account for sticky header/tabs - subtract offset so element appears below sticky header
          let stickyHeaderOffset = 0;
          if (!isLaptopOrBigger && stickyTabs) {
            // On mobile, account for the sticky tabs group height
            stickyHeaderOffset = stickyTabs.offsetHeight;
          }
          const scrollOffset =
            elementOffsetTop -
            stickyHeaderOffset -
            (isLaptopOrBigger ? 100 : 50); // 20px padding

          container.scrollTo({
            top: Math.max(0, scrollOffset),
            behavior: "smooth",
          });
        }
      }, 100);
    },
    [isLaptopOrBigger],
  );

  useEffect(() => {
    setTimeout(() => {
      if (!activeUser) {
        Navigation.navigate("/login");
      }
    }, 3000);
  }, [activeUser]);

  const tabs = useMemo(() => {
    const totalAssignments = Object.keys(userAssignments).length;
    const incompleteAssignments = userIncompleteAssignments.length;
    const totalVideosWatched = userVideosWatched.length;

    const PlaylistIcon = ResourceConfigs.playlist.Icon;
    const AssignmentIcon = ResourceConfigs.assignment.Icon;

    return [
      {
        value: "levels",
        label: "Levels",
        Icon: IconStarFilled,
        total: `Level ${currentUserLevel?.level ? currentUserLevel?.level : totalUserLevels} of ${totalUserLevels}`,
        render: () => <UserProfileLevels />,
      },
      {
        value: "playlist-recs",
        label: "Playlists for You",
        Icon: IconSparkles,
        render: () => (
          <UserProfileRecommendedPlaylists
            onClick={() => {
              setActiveTab("playlist-recs");
              scrollToSection("playlist-recs");
            }}
          />
        ),
        total: Object.keys(recommendedPlaylists).length,
      },
      {
        value: "advizer-recs",
        label: "Advizers for You",
        Icon: IconUsersGroup,
        render: () => (
          <UserProfileRecommendedAdvizers
            onClick={() => {
              setActiveTab("advizer-recs");
              scrollToSection("advizer-recs");
            }}
          />
        ),
        total: totalUserRecommendedAdvizers,
      },

      {
        value: null,
      },

      {
        value: "reactions",
        label: "Reactions",
        Icon: IconThumbUp,
        total:
          totalPositiveVideoReactions +
          totalNeutralVideoReactions +
          totalNegativeVideoReactions,
        render: () => <UserProfileReactions />,
      },
      {
        value: "playlists-in-progress",
        label: "Active Playlists",
        Icon: PlaylistIcon,
        total: inProgressPlaylistKeys.length,
        render: () => <UserProfilePlaylists />,
      },
      {
        value: "assignments",
        label: "Active Assignments",
        Icon: IconClock,
        total: totalAssignments,
        render: () => <UserProfileAssignments showCompleted={false} />,
      },
      {
        value: null,
      },

      {
        value: "video-history",
        label: "Video History",
        Icon: IconVideo,
        render: () => <UserProfileVideosWatched />,
        total: totalVideosWatched,
      },
      {
        value: "advizer-history",
        label: "Advizer History",
        Icon: IconUser,
        render: () => <UserProfileVideosWatched advizersOnly={true} />,
        total: userAdvizersWatched.length,
      },
      {
        value: "playlists-completed",
        Icon: IconCheckbox,
        label: "Completed Playlists",
        total: completedPlaylistKeys.length,
        render: () => <UserProfilePlaylists showCompletedOnly />,
      },
      {
        value: "assignments-completed",
        label: "Completed Assignments",
        Icon: AssignmentIcon,
        total: totalAssignments - incompleteAssignments,
        render: () => <UserProfileAssignments showCompleted={true} />,
      },
    ];
  }, [
    completedPlaylistKeys.length,
    currentUserLevel?.level,
    inProgressPlaylistKeys.length,
    recommendedPlaylists,
    scrollToSection,
    totalNegativeVideoReactions,
    totalNeutralVideoReactions,
    totalPositiveVideoReactions,
    totalUserLevels,
    totalUserRecommendedAdvizers,
    userAdvizersWatched.length,
    userAssignments,
    userIncompleteAssignments.length,
    userVideosWatched.length,
  ]);

  // Handle initial hash on page load and hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the #
      if (hash && tabs.some((tab) => tab.value === hash)) {
        setActiveTab(hash);
        // Wait for content to render before scrolling
        setTimeout(() => {
          scrollToSection(hash);
        }, 300);
      }
    };

    // Handle initial hash on page load
    handleHashChange();

    // Listen for hash changes (browser back/forward)
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [tabs, scrollToSection]);

  const TabsOptions = useMemo(() => {
    if (isLaptopOrBigger) {
      return (
        <Stack p="lg" gap={0} w="100%">
          <Text titleStyle="h3" mb="sm">
            My Advize Profile
          </Text>
          {tabs.map((tab, index) => {
            if (!tab.value) {
              return <Divider key={tab.value || index} my="xs" />;
            }
            const isActive = activeTab === tab.value;
            return (
              <Link
                key={tab.value}
                to={`/profile#${tab.value}`}
                ariaLabel={tab.label}
                radius="sm"
                onClick={() => {
                  setActiveTab(tab.value as any);
                  Navigation.navigate(`/profile#${tab.value}`);
                  scrollToSection(tab.value);
                  Analytics.click({
                    context: "User profile",
                    action: "User profile menu item > Desktop",
                    label: tab.label,
                  });
                }}
                style={{
                  backgroundColor: isActive
                    ? theme.colors.primary[0]
                    : "transparent",
                  color: isActive
                    ? theme.colors.primary[7]
                    : theme.colors.slate[7],
                  padding: "12px 16px",
                }}
              >
                <Group justify="space-between" w="100%" flex={1}>
                  <Group gap="xs" align="center">
                    {tab.Icon && (
                      <tab.Icon
                        size={28}
                        color={
                          isActive
                            ? theme.colors.primary[7]
                            : theme.colors.slate[7]
                        }
                      />
                    )}
                    <Text ta="left" fz="18px">
                      {tab.label}
                    </Text>
                  </Group>
                  {tab.total && (
                    <Badge
                      color={isActive ? "primary" : "gray"}
                      ariaLabel={tab.total.toString()}
                      size="md"
                      radius="xl"
                    >
                      {tab.total}
                    </Badge>
                  )}
                </Group>
              </Link>
            );
          })}
        </Stack>
      );
    }

    const selectedTab = tabs.find((tab) => tab.value === activeTab);
    const SelectedIcon = selectedTab?.Icon;
    const selectedTotal = selectedTab?.total;

    // Custom render function for Select dropdown options
    const renderSelectOption: SelectProps["renderOption"] = ({ option }) => {
      const Icon = (option as any).icon;
      const total = (option as any).total;

      return (
        <Group justify="space-between" w="100%">
          <Group gap="xs" align="center">
            {Icon && <Icon size={28} color={theme.colors.slate[7]} />}
            <Text ta="left" fz="18px">
              {option.label}
            </Text>
          </Group>
          {total !== undefined && total !== null && (
            <Badge
              color="primary"
              ariaLabel={total.toString()}
              size="md"
              radius="xl"
            >
              {total}
            </Badge>
          )}
        </Group>
      );
    };

    return (
      <Group w="100%" p="sm">
        <Select
          value={activeTab}
          onChange={(value) => {
            if (value) {
              setActiveTab(value as any);
              scrollToSection(value);
              const selectedTab = tabs.find((tab) => tab.value === value);
              if (selectedTab) {
                Analytics.click({
                  context: "User profile",
                  action: "User profile menu item > Mobile",
                  label: selectedTab.label,
                });
              }
            }
          }}
          w="100%"
          // maw={400}
          size="md"
          renderOption={renderSelectOption}
          leftSection={
            SelectedIcon ? (
              <SelectedIcon size={28} color={theme.colors.primary[7]} />
            ) : undefined
          }
          rightSection={
            selectedTotal !== undefined && selectedTotal !== null ? (
              <Badge
                color="primary"
                ariaLabel={selectedTotal.toString()}
                radius="xl"
              >
                {selectedTotal}
              </Badge>
            ) : undefined
          }
          data={tabs
            .filter((tab) => tab.value !== null)
            .map((tab) => ({
              value: tab.value!,
              label: tab.label,
              icon: tab.Icon,
              total: tab.total,
            }))}
        />
      </Group>
    );
  }, [
    activeTab,
    isLaptopOrBigger,
    tabs,
    theme.colors.primary,
    theme.colors.slate,
    scrollToSection,
  ]);

  const MainContent = useMemo(() => {
    return (
      <Stack px="xl" gap={80}>
        {tabs.map((tab) =>
          tab.value ? (
            <Stack id={tab.value} key={tab.value} gap={4}>
              <Group align="center" gap="xs" mb={tab.total ? "sm" : 0}>
                {tab.Icon && (
                  <tab.Icon
                    size={30}
                    color={
                      !tab.total
                        ? theme.colors.slate[7]
                        : theme.colors.primary[9]
                    }
                  />
                )}
                <Title
                  order={3}
                  size="h2"
                  c={!tab.total ? "slate.7" : "primary.9"}
                  pt={4}
                >
                  {tab.label}
                </Title>
                {tab.total && (
                  <Badge
                    color={
                      typeof tab.total === "number" && tab.total > 0
                        ? "primary"
                        : "gray"
                    }
                    ariaLabel={tab.total.toString()}
                    radius="xl"
                    size="lg"
                    ml="md"
                    fz={14}
                    // w={24}
                    // h={24}
                    // px="md"
                    // py={0}
                  >
                    {tab.total}
                  </Badge>
                )}
              </Group>
              {tab.render?.() ?? null}
            </Stack>
          ) : null,
        )}
      </Stack>
    );
  }, [tabs, theme.colors]);

  if (!activeUser) {
    return <LoadingScreen />;
  }

  if (isLaptopOrBigger) {
    return (
      <Stack
        pr={isTabletOrBigger ? "xl" : 0}
        h="100vh"
        w="100%"
        bg={theme.colors.background[1]}
        style={{ overflow: "hidden" }}
        gap={0}
      >
        <Group
          align="flex-start"
          gap={0}
          h="100%"
          style={{ overflow: "hidden" }}
        >
          <Stack
            w={350}
            h="100vh"
            bg="background.2"
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              position: "sticky",
              top: 0,
            }}
            gap={0}
            pt={styles.dimensions.headerHeight}
          >
            {TabsOptions}
          </Stack>

          {/* Main content area */}
          <Stack
            ref={scrollContainerRef}
            flex={1}
            h="100%"
            pb={100}
            pt={`${styles.dimensions.headerHeight + 40}px`}
            px={0}
            style={{
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {MainContent}
          </Stack>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack
      w="100%"
      h="100vh"
      style={{
        overflow: "hidden",
      }}
      gap={0}
      bg="background.1"
    >
      <Group
        ref={stickyTabsRef}
        py={0}
        justify="flex-start"
        align="flex-start"
        gap="xs"
        pos="sticky"
        top={styles.dimensions.headerHeight}
        style={{ zIndex: 10 }}
        bg="background.1"
        w="100%"
      >
        {TabsOptions}
      </Group>

      <Stack
        ref={scrollContainerRef}
        pt={60}
        pb={100}
        px={0}
        w="100%"
        flex={1}
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
      >
        {MainContent}
      </Stack>
    </Stack>
  );
}
